import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../api";
import { useAuth } from "../context/AuthContext";
import DataTable from "../components/DataTable";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtDate, fmtMoney, fmtNum, todayISO } from "../utils/format";

export default function Purchases() {
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", account_id: "", date: todayISO() });
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const delPurchase = async (p) => {
    if (!window.confirm(`Supprimer l'achat ${p.purchase_number} ? Les totaux fournisseur seront ajustés et le prix d'achat du véhicule remis à 0.`)) return;
    try {
      await api.delete(`/purchases/${p.id}`);
      toast.success("Achat supprimé");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const [form, setForm] = useState({ supplier_id: "", vehicle_id: "", purchase_date: todayISO(), price: "", currency: "FCFA", exchange_rate: "", paid_amount: "", payment_method: "Virement bancaire", due_date: "", account_id: "", notes: "" });

  const load = () => api.get("/purchases").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/vehicles").then((r) => setVehicles(r.data.filter((v) => v.status !== "Vendu")));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
    api.get("/accounts").then((r) => setAccounts(r.data));
    api.get("/meta").then((r) => setMeta(r.data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.vehicle_id || !form.price) return toast.error("Véhicule et prix requis");
    try {
      await api.post("/purchases", { ...form, price: Number(form.price), paid_amount: Number(form.paid_amount) || 0, exchange_rate: form.exchange_rate ? Number(form.exchange_rate) : null, account_id: form.account_id || null, due_date: form.due_date || null });
      toast.success("Achat enregistré");
      setOpen(false);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const submitPay = async () => {
    try {
      await api.post(`/purchases/${payFor.id}/pay`, { amount: Number(payForm.amount), account_id: payForm.account_id || null, date: payForm.date });
      toast.success("Paiement fournisseur enregistré");
      setPayFor(null);
      setPayForm({ amount: "", account_id: "", date: todayISO() });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-5" data-testid="purchases-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Achats</h1>
          <p className="mt-1 text-sm text-slate-500">Achats de véhicules, paiements fournisseurs et achats à crédit.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-purchase-button"><Plus className="mr-1 h-4 w-4" /> Nouvel achat</Button></DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Enregistrer un achat</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Véhicule *</Label>
                <Select value={form.vehicle_id} onValueChange={(v) => set("vehicle_id", v)}>
                  <SelectTrigger data-testid="purchase-vehicle-select"><SelectValue placeholder="Choisir un véhicule…" /></SelectTrigger>
                  <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.internal_number} — {v.brand} {v.model}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Fournisseur</Label>
                <Select value={form.supplier_id} onValueChange={(v) => set("supplier_id", v)}>
                  <SelectTrigger data-testid="purchase-supplier-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} {s.country ? `(${s.country})` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Date d'achat</Label><Input type="date" value={form.purchase_date} onChange={(e) => set("purchase_date", e.target.value)} data-testid="purchase-date" /></div>
              <div><Label>Prix d'achat *</Label><Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="purchase-price" /></div>
              <div><Label>Devise</Label>
                <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                  <SelectTrigger data-testid="purchase-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>{(meta?.currencies || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.currency !== "FCFA" && (
                <div><Label>Taux (défaut {meta?.rates?.[form.currency]})</Label><Input type="number" value={form.exchange_rate} onChange={(e) => set("exchange_rate", e.target.value)} data-testid="purchase-rate" /></div>
              )}
              <div><Label>Montant payé (FCFA)</Label><Input type="number" value={form.paid_amount} onChange={(e) => set("paid_amount", e.target.value)} data-testid="purchase-paid" /></div>
              <div><Label>Mode de paiement</Label>
                <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{(meta?.payment_methods || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Compte débité</Label>
                <Select value={form.account_id} onValueChange={(v) => set("account_id", v === "none" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Aucun</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Échéance du solde</Label><Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="purchase-due-date" /></div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="purchase-submit">Enregistrer l'achat</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <DataTable testidPrefix="purchases-table" rows={rows}
        searchKeys={["purchase_number", "supplier_name", "vehicle_label"]}
        columns={[
          { key: "purchase_number", label: "N°", render: (r) => <span className="font-mono-num font-semibold">{r.purchase_number}</span> },
          { key: "purchase_date", label: "Date", render: (r) => fmtDate(r.purchase_date) },
          { key: "vehicle_label", label: "Véhicule" },
          { key: "supplier_name", label: "Fournisseur" },
          { key: "price_fcfa", label: "Prix", className: "text-right", render: (r) => (
            <div className="text-right"><p className="font-mono-num font-medium">{fmtMoney(r.price_fcfa)}</p>
            {r.currency !== "FCFA" && <p className="text-xs text-slate-400">{fmtNum(r.price)} {r.currency}</p>}</div>
          ) },
          { key: "paid_amount", label: "Payé", className: "text-right", render: (r) => <span className="font-mono-num">{fmtMoney(r.paid_amount)}</span> },
          { key: "remaining", label: "Reste", className: "text-right", render: (r) => <span className={`font-mono-num font-semibold ${r.remaining > 0 ? "text-red-600" : "text-emerald-600"}`}>{fmtMoney(r.remaining)}</span> },
          { key: "due_date", label: "Échéance", render: (r) => fmtDate(r.due_date) },
          { key: "actions", label: "", sortable: false, render: (r) => (
            <div className="flex gap-1">
              {r.remaining > 0 && (
                <Button size="sm" variant="outline" data-testid={`pay-purchase-${r.purchase_number}`} onClick={() => { setPayFor(r); setPayForm({ amount: String(r.remaining), account_id: "", date: todayISO() }); }}>Régler</Button>
              )}
              {isAdmin && <Button size="sm" variant="ghost" className="text-red-500" title="Supprimer l'achat" onClick={() => delPurchase(r)} data-testid={`delete-purchase-${r.purchase_number}`}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          ) },
        ]}
      />

      <Dialog open={!!payFor} onOpenChange={() => setPayFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Règlement fournisseur — {payFor?.purchase_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Montant (FCFA)</Label><Input type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="pay-purchase-amount" /></div>
            <div><Label>Date</Label><Input type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} /></div>
            <div><Label>Compte</Label>
              <Select value={payForm.account_id} onValueChange={(v) => setPayForm({ ...payForm, account_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="pay-purchase-account"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Aucun</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={submitPay} data-testid="pay-purchase-submit">Valider le paiement</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
